// scale.js — OscillaScore Scale Cue (uniform & non-uniform)

import { registerAnimation, autoStartAnimation } from "./animation.js";
import { readPivot, isOff } from "../parser/cueUtils.js";
import { isObjectTrig } from "./probeTriggers.js";
import { scheduleCueStart } from "./cueDispatcher.js";
import { createHitLabel, shouldCreateHitLabel, repositionHitLabelFast, repositionChildHitLabels } from "./animOverlays.js";


import { applyPrestateBeforeStart, applyPrestateOnStart, ensureAnimWrapper,
    armElement, needsArming, installOscToggleHandler, installToggleStatesHandler, isOscEnabled, hintWrapperCompositing } from "./animShared.js";

import { createOscOverlay } from "./osc.js";
import { makeValueGenerator, evalMaybeRandom, isRandomised } from "./audio/audioShared.js";
import { sendOSC } from "../system/oscillaOSCClient.js";
import { publish, bindParam } from '../control/paramBinding.js';
import { animeEase } from '../control/easing.js';
import { setupSpeedBinding, isSignalRef, isOscRef, registerFaderDefault } from './cueParamBinding.js';
import { createAnimationLoop, createAlternatingLoop } from './cueAnimLoop.js';
import { secs, secPerBeat } from './beats.js';

// ============================================================
// Utilities (pivot + current scale)
// ============================================================
function applySvgPivot(el, pivot = null) {
    // The same pivot rotate() takes: rotate(pivot:[x, y]) or a data-pivot
    // attribute. An object that turns about a point off itself usually wants
    // to grow from that point too.
    // The attribute is authored on the element; the cue animates a wrapper it
    // made INSIDE or around that element, which carries no attribute of its
    // own — so look up to the host, or the score scales about its bounding
    // box instead of its point.
    const host = el.closest?.("[data-pivot]") || el;
    const attr = readPivot(host.getAttribute?.("data-pivot"));
    const fixed = readPivot(pivot) || attr;
    if (fixed) {
        el.style.transformOrigin = `${fixed.x}px ${fixed.y}px`;
        return;
    }
    if (!el.getBBox) return;
    const bb = el.getBBox();
    // a straight line has a middle too: only a sizeless box has none
    if (!bb || (bb.width === 0 && bb.height === 0)) return;
    const cx = bb.x + bb.width / 2;
    const cy = bb.y + bb.height / 2;
    el.style.transformOrigin = `${cx}px ${cy}px`;
}

// ======================================================================
//  SCALE SEQUENCE ENGINE
//  handleScaleSequence(el, cfg)
// ======================================================================
/**
 * Whatever scale runs on this element gives way to a new one: a pulse's loop
 * is stopped, a sequence's step chain halted (its tween and its hold). Both
 * write the same wrapper, so two left running fight over it frame by frame.
 */
function stopScaleOn(el) {
    const prev = el._oscillaScaleAnim;
    if (prev?.stop) prev.stop();        // a continuous loop
    else clearTimeout(prev);            // a sequence's hold (its tween: below)
    el._oscillaScaleAnim = null;
    el._oscillaScaleSeq?.stop();
    el._oscillaScaleSeq = null;
}

export function handleScaleSequence(el, cfg) {
    stopScaleOn(el);
    cfg._stopped = false;               // a fresh run un-retires this cfg

    const astArgs = cfg.astArgs || [];
    const k = secPerBeat();             // dur: and hold: are beats

    // Value sources
    let ax = cfg.xValues || null;
    let ay = cfg.yValues || null;
    let xPattern = cfg.xPattern || null;
    let yPattern = cfg.yPattern || null;
    let uniformPattern = cfg.pattern || null;

    // Duration
    let dur = 1;
    let durGen = null;

    // Behaviour
    let dir = 1;
    let mode = "loop";
    let interp = "smooth";
    let ease = "linear";
    let hold = 0;
    let loop = 0;          // 0 = forever, n = n passes over the values

    // OSC
    let oscMode = 0;
    let oscAddr = null;

    let axGen = null;
    let ayGen = null;

    // ------------------------------------------------------------
    // Parse AST
    // ------------------------------------------------------------
    for (const arg of astArgs) {
        const key = arg.key || arg.type;
        const val = arg.value;

        switch (key) {

            case "values":
                if (val && val.type === "pattern") {
                    // one generator: y takes x's step (nextPair), so a
                    // Prand does not draw the two axes apart
                    uniformPattern = val;
                    axGen = makeValueGenerator(val);
                    ayGen = null;
                    ax = null; ay = null;
                } else if (Array.isArray(val)) {
                    ax = val.slice();
                    ay = val.slice();
                }
                break;

            case "x":
            case "valuesX":
                if (val && val.type === "pattern") {
                    xPattern = val;
                    axGen = makeValueGenerator(val);
                    ax = null;
                } else if (Array.isArray(val)) {
                    ax = val.slice();
                }
                break;

            case "y":
            case "valuesY":
                if (val && val.type === "pattern") {
                    yPattern = val;
                    ayGen = makeValueGenerator(val);
                    ay = null;
                } else if (Array.isArray(val)) {
                    ay = val.slice();
                }
                break;

            case "dur": {
                const d = secs(val, k);
                if (d && d.type === "pattern") {
                    durGen = makeValueGenerator(d);
                } else if (Array.isArray(d)) {
                    durGen = makeValueGenerator({ values: d });
                } else {
                    dur = Number(d) || 1;
                    cfg.dur = dur;  // Store for network sync
                }
                break;
            }

            case "dir":
                dir = Number(val) === -1 ? -1 : 1;
                break;

            case "mode":
                mode = String(val).trim().toLowerCase();
                break;

            case "interp":
                interp = String(val).trim().toLowerCase();
                break;

            case "pivot":
                cfg.pivot = val;
                break;
            case "ease":
                ease = String(val).trim();
                break;

            case "hold":
                hold = Number(secs(val, k)) || 0;
                break;

            case "loop":
                loop = Number(val) || 0;
                break;

            case "osc":
                oscMode = Number(val) || 0;
                break;

            case "oscaddr":
                oscAddr = String(val).trim();
                break;
        }
    }

    // fallback osc address
    if (!oscAddr) {
        oscAddr = `scale/${cfg.uid}`;
    }

    // dir:-1 reverses the value sequence
    if (dir === -1) {
        if (Array.isArray(ax)) ax = ax.slice().reverse();
        if (Array.isArray(ay)) ay = ay.slice().reverse();
    }

    // generators if needed
    if (xPattern && !axGen) axGen = makeValueGenerator(xPattern);
    if (yPattern && !ayGen) ayGen = makeValueGenerator(yPattern);
    if (uniformPattern && !axGen && !ayGen) axGen = makeValueGenerator(uniformPattern);

    const wrapper = ensureAnimWrapper(el);
    applySvgPivot(wrapper, cfg?.pivot);

    const NX = Array.isArray(ax) ? ax.length : Infinity;
    const NY = Array.isArray(ay) ? ay.length : Infinity;
    const N  = Math.max(NX, NY);

    let index = 0;
    let direction = 1;
    let cycles = 0;

    // driver: from the size it has (a sequence run on a scaled shape went
    // back to 1 first, then on to its first value)
    const had = String(wrapper.style.transform || "").match(/scale\(\s*([-\d.e]+)(?:\s*,\s*([-\d.e]+))?\s*\)/);
    const driver = had ? { sx: Number(had[1]), sy: Number(had[2] ?? had[1]) } : { sx: 1, sy: 1 };

    // On the signal bus for follow(): sx, sy, uniform (their mean) and t --
    // where it is between the smallest and largest it has been sent to, 0..1,
    // so follow(x.t, 200, 4000) sweeps the whole range whatever the values.
    let _lo = Infinity, _hi = -Infinity;
    const publishScale = (sx, sy) => {
        const u = (sx + sy) / 2;
        const t = _hi > _lo ? Math.min(1, Math.max(0, (u - _lo) / (_hi - _lo))) : 0;
        publish("scale", cfg.uid, { sx, sy, uniform: u, t });
    };

    // ------------------------------
    // Overlay (centered, only if OSC)
    // ------------------------------
    if (cfg._overlay) {
        cfg._overlay.destroy();
        cfg._overlay = null;
    }

    if (isOscEnabled(cfg, oscMode)) {
        cfg._overlay = createOscOverlay({
            anchorEl: el,
            label: oscAddr,
            track: true
        });
    }

    // ------------------------------
    // Helpers
    // ------------------------------
    function nextPair() {
        let sx, sy;

        if (axGen) sx = axGen.next();
        else if (Array.isArray(ax)) sx = ax[index % NX];
        else sx = driver.sx;

        if (ayGen) sy = ayGen.next();
        else if (Array.isArray(ay)) sy = ay[index % NY];
        else sy = sx;

        if (sx == null || sy == null) return null;
        // rand()/irand() steps arrive as funcCall objects — evaluate here.
        // sy === sx means the author wrote one uniform values: list, so the
        // two axes are the same step and must share one roll; evaluated
        // separately, a live rand() would scale x and y independently.
        const nx = Number(evalMaybeRandom(sx));
        const ny = (sy === sx) ? nx : Number(evalMaybeRandom(sy));
        if (!Number.isFinite(nx) || !Number.isFinite(ny)) return null;
        return [nx, ny];
    }

    function stepIndexAdvance() {
        if (axGen || ayGen) return;

        if (mode === "alternate") {
            index += direction;
            if (index >= N || index < 0) {
                direction *= -1;
                index += direction;
                cycles++;
            }
        } else {
            index = (index + 1) % N;
            if (index === 0) cycles++;
        }
    }

    // ------------------------------
    // Observer pause/resume state
    // ------------------------------
    let _paused = false;
    let _halted = false;            // stop(): a later scale took the element over
    let _currentTween = null;
    let _holdStart = 0;
    let _holdRemaining = 0;
    let _skipRun = 0;   // consecutive equal-target skips (bounded recursion guard)

    // Never let an error escape into anime's engine frame: a throw from a
    // complete callback aborts anime's shared RAF loop and it does not
    // reschedule — freezing every anime animation on the page.
    function safeRunNext() {
        try { runNext(); }
        catch (e) { console.error(`[scale] sequence step failed for ${cfg.uid}:`, e); }
    }

    // ------------------------------
    // Main loop
    // ------------------------------
    function runNext() {

        if (_paused || _halted) return;
        if (loop > 0 && cycles >= loop) return;

        const pair = nextPair();
        if (!pair) return;

        let [tgtX, tgtY] = pair;
        { const u = (Number(tgtX) + Number(tgtY)) / 2; if (Number.isFinite(u)) { _lo = Math.min(_lo, u); _hi = Math.max(_hi, u); } }

        const durRaw = durGen ? evalMaybeRandom(durGen.next()) : dur;
        const stepDur = Number(durRaw) || dur || 0.0001;

        // ✨ if target equals current → skip instantly — but BOUNDED. A
        // one-element or all-equal sequence (e.g. valuesX:[1.3] with
        // mode:alternate) would otherwise recurse here forever: stack overflow
        // thrown from inside a tween's complete callback = inside anime's
        // engine frame, which kills the shared engine loop for EVERY anime
        // animation on the page. After a full pass of equal targets, treat it
        // as a hold and re-check after stepDur instead of recursing.
        if (driver.sx === tgtX && driver.sy === tgtY) {
            stepIndexAdvance();
            _skipRun++;
            if (_skipRun >= Math.max(2, Number.isFinite(N) ? N : 8)) {
                _skipRun = 0;
                el._oscillaScaleAnim = setTimeout(runNext, stepDur * 1000);
                return;
            }
            runNext();
            return;
        }
        _skipRun = 0;

        // ---------------------------------
        // STEP MODE
        // ---------------------------------
        if (interp === "step") {
            driver.sx = tgtX;
            driver.sy = tgtY;

            wrapper.style.transform = `scale(${tgtX}, ${tgtY})`;
            publishScale(tgtX, tgtY);

            if (isOscEnabled(cfg, oscMode)) {
                sendOSC({
                    addr: oscAddr,
                    type: "osc_scale",
                    uid: cfg.uid,
                    sx: tgtX,
                    sy: tgtY,
                    avg: (tgtX + tgtY) / 2,
                    norm: Math.min(1, Math.max(0, (tgtX - 1) / 3)),
                    timestamp: Date.now()
                });

                cfg._overlay?.update(
                    `sx:${tgtX.toFixed(2)} sy:${tgtY.toFixed(2)}`
                );
            }

            stepIndexAdvance();

            // 🔥 no extra RAF — only one timer
            _holdStart = performance.now();
            _holdRemaining = stepDur * 1000;
            el._oscillaScaleAnim = setTimeout(() => {
                _holdRemaining = 0;
                runNext();
            }, stepDur * 1000);
            return;
        }

        // ---------------------------------
        // SMOOTH MODE
        // ---------------------------------
        const anim = anime.animate(driver, {
            sx: tgtX,
            sy: tgtY,
            duration: stepDur * 1000,
            ease: animeEase(ease),

            onUpdate: (self) => {
                wrapper.style.transform = `scale(${driver.sx}, ${driver.sy})`;
                publishScale(driver.sx, driver.sy);

                // Store for network sync
                cfg._currentScale = { sx: driver.sx, sy: driver.sy };
                cfg._currentT = self.progress;

                if (isOscEnabled(cfg, oscMode)) {
                    sendOSC({
                        addr: oscAddr,
                        type: "osc_scale",
                        uid: cfg.uid,
                        sx: driver.sx,
                        sy: driver.sy,
                        avg: (driver.sx + driver.sy) / 2,
                        norm: Math.min(1, Math.max(0, (driver.sx - 1) / 3)),
                        timestamp: Date.now()
                    });

                    cfg._overlay?.update(
                        `sx:${driver.sx.toFixed(2)} sy:${driver.sy.toFixed(2)}`
                    );
                }
            },

            onComplete: () => {
                stepIndexAdvance();

                if (hold > 0) {
                    _holdStart = performance.now();
                    _holdRemaining = hold * 1000;
                    el._oscillaScaleAnim = setTimeout(() => {
                        _holdRemaining = 0;
                        safeRunNext();
                    }, hold * 1000);
                } else {
                    safeRunNext();
                }
            }
        });

        el._oscillaScaleAnim = anim;
        _currentTween = anim;
    }


    requestAnimationFrame(runNext);

    // ---------------------------------------------------------------
    // Observer pause/resume wrapper
    // Lets the visibility observer halt the entire step chain,
    // not just the current anime tween.
    // ---------------------------------------------------------------
    cfg._anim = {
        pause() {
            _paused = true;
            clearTimeout(el._oscillaScaleAnim);
            if (_holdRemaining > 0) {
                _holdRemaining = Math.max(0, _holdRemaining - (performance.now() - _holdStart));
            }
            if (_currentTween && typeof _currentTween.pause === 'function') {
                _currentTween.pause();
            }
        },
        stop() {
            _paused = true;
            _halted = true;
            clearTimeout(el._oscillaScaleAnim);
            el._oscillaScaleAnim = null;
            _holdRemaining = 0;
            if (_currentTween && typeof _currentTween.pause === 'function') {
                _currentTween.pause();
            }
        },
        play() {
            if (cfg._stopped || _halted) return;   // retired by a reset — see trans.js _anim.stop()
            if (!_paused) return;
            _paused = false;

            if (_holdRemaining > 0) {
                _holdStart = performance.now();
                el._oscillaScaleAnim = setTimeout(() => {
                    _holdRemaining = 0;
                    runNext();
                }, _holdRemaining);
            }
            else if (_currentTween && typeof _currentTween.play === 'function'
                     && !_currentTween.completed) {
                _currentTween.play();
            }
            else {
                runNext();
            }
        }
    };
    el._oscillaScaleSeq = cfg._anim;    // for stopScaleOn
}




// ============================================================
// Continuous fallback (pulse) if no values provided
// Uses shared animation loop helper for dynamic speed control
// ============================================================
export function handleScaleContinuous(el, cfg) {

    const astArgs = cfg.astArgs || [];

    let dir = 1;
    let durValue = 2;
    let durBinding = null;
    let loop = 0;
    let oscMode = 0;
    let mode = "alternate";   // default: smooth ping-pong, no wrap jump
    let oscaddr = null;

    function oscEnabled(cfg) {
        return cfg._oscEnabled === true || oscMode > 0;
    }

    for (const arg of astArgs) {
        const key = arg.key || arg.type;
        const val = arg.value;

        if (key === "dir") dir = Number(val);
        if (key === "dur") {
            const d = secs(val);                // beats
            if (isSignalRef(d)) {
                durBinding = d;
                durValue = d.range?.[0] ?? 2;
            } else {
                durValue = Number(d) || 2;
            }
            cfg.dur = durValue;
        }
        if (key === "loop") loop = Number(val);
        if (key === "osc") oscMode = Number(val) || 0;
        if (key === "mode") {
            mode = String(val).toLowerCase();
            if (mode === "alt") mode = "alternate";
        }
        if (key === "oscaddr") oscaddr = String(val).trim();
    }

    if (!oscaddr) oscaddr = `scale/${cfg.uid || "unknown"}`;

    // Set up speed binding
    const speedBinding = setupSpeedBinding(cfg, durBinding || durValue, {
        kind: 'scale',
        baseDur: durValue,
        minDur: 0.5,
        maxDur: 60
    });

    stopScaleOn(el);

    const animEl = ensureAnimWrapper(el);
    applySvgPivot(animEl, cfg?.pivot);

    if (cfg._overlay) cfg._overlay.destroy();

    if (oscEnabled(cfg)) {
        cfg._overlay = createOscOverlay({
            anchorEl: el,
            label: cfg.uid || "scale"
        });
        cfg._overlay.update("...");
        cfg._overlay.position();
    }

    function emitScaleOSC(sx, sy) {
        if (!oscEnabled(cfg)) return;
        sendOSC({
            type: "osc_scale",
            uid: cfg.uid,
            addr: oscaddr,
            sx: Number(sx),
            sy: Number(sy),
            avg: (Number(sx) + Number(sy)) / 2,
            timestamp: Date.now()
        });
    }

    const minScale = 1;
    const maxScale = 1.5;

    // Choose loop type based on mode
    const createLoop = mode === "alternate" ? createAlternatingLoop : createAnimationLoop;

    const animLoop = createLoop({
        dur: durValue,
        loop,
        getSpeed: () => cfg.speedMultiplier ?? 1,

        onTick: (phase, dt, altDir) => {
            // dir:-1 reverses the phase; alternate mode also uses its own direction
            let t = phase;
            if (dir === -1) t = 1 - t;
            if (mode === "alternate" && altDir === -1) t = 1 - t;

            const sx = minScale + (maxScale - minScale) * t;
            const sy = sx; // Uniform scaling

            animEl.style.transform = `scale(${sx}, ${sy})`;

            repositionHitLabelFast(cfg.uid);
            repositionChildHitLabels(animEl); // Update labels for child elements

            if (cfg._overlay) {
                cfg._overlay.update(`sx:${sx.toFixed(2)} sy:${sy.toFixed(2)}`);
            }

            emitScaleOSC(sx, sy);

            publish("scale", cfg.uid, {
                sx: sx,
                sy: sy,
                uniform: (sx + sy) / 2
            });

            cfg._currentScale = { sx, sy };
            cfg._currentT = phase;
        },

        onComplete: () => {
            speedBinding.unbind();
        }
    });

    el._oscillaScaleAnim = animLoop;
    cfg._anim = animLoop;

    animLoop.start();
}

// ============================================================================
// SIGNAL-BOUND SCALE MODE
// Direct fader control of scale - no animation, just real-time updates
// ============================================================================
export function handleScaleSignalBound(el, cfg) {
    const scaleWrapper = cfg._scaleWrapper || el;
    const astArgs = cfg.astArgs || [];

    // Get sx and sy signal refs
    let sxRef = null;
    let syRef = null;
    let sxDefault = 1;
    let syDefault = 1;
    let decayTime   = 0;   // decay back to 1.0 after each hit (beat-visualiser); beats, read as seconds
    let decayEasing = 'easeOutExpo';

    let sxHasExplicitDefault = false;
    let syHasExplicitDefault = false;

    for (const a of astArgs) {
        const key = a.key || a.type;
        if (key === "sx" && (isSignalRef(a.value) || isOscRef(a.value))) {
            sxRef = a.value;
            sxHasExplicitDefault = a.value.default !== undefined;
            sxDefault = a.value.default ?? a.value.range?.[0] ?? 1;
        }
        if (key === "sy" && (isSignalRef(a.value) || isOscRef(a.value))) {
            syRef = a.value;
            syHasExplicitDefault = a.value.default !== undefined;
            syDefault = a.value.default ?? a.value.range?.[0] ?? 1;
        }
        // both axes from one signal: scale(follow(bass.t, 0.2, 1.5)) or
        // values:follow(...) -- the key an even scale already takes -- and
        // uniform: (its outlet's name). A lone sx: is x only.
        if ((key === "values" || key === "uniform") && (isSignalRef(a.value) || isOscRef(a.value))) {
            sxRef = syRef = a.value;
            sxHasExplicitDefault = syHasExplicitDefault = a.value.default !== undefined;
            sxDefault = syDefault = a.value.default ?? a.value.range?.[0] ?? 1;
        }
        if (key === "decay")    decayTime    = Number(secs(a.value)) || 0;
        if (key === "ease")     decayEasing  = String(a.value);
    }

    // Current scale values
    let currentSx = sxDefault;
    let currentSy = syDefault;

    // Apply proper SVG pivot point
    applySvgPivot(scaleWrapper, cfg?.pivot);

    // translateZ(0) creates an explicit GPU compositing layer which causes Chromium
    // to cache the SVG raster at the pre-transform resolution — the exact problem we
    // are trying to avoid. Only use it on Safari where omitting it causes the layer
    // to be culled at large scale values.
    const _isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const _layerHint = _isSafari ? ' translateZ(0)' : '';
    hintWrapperCompositing(scaleWrapper);
    scaleWrapper.style.transform = `scale(${currentSx}, ${currentSy})${_layerHint}`;

    // Update function
    function updateScale() {
        scaleWrapper.style.transform = `scale(${currentSx}, ${currentSy})${_layerHint}`;

        repositionHitLabelFast(cfg.uid);
        repositionChildHitLabels(scaleWrapper); // Update labels for child elements

        // Publish for other systems; t: where it is in the follow() range
        // (0..1), as a values: scale publishes it
        const u = (currentSx + currentSy) / 2;
        const rng = (sxRef || syRef)?.range;
        const t = Array.isArray(rng) && rng[1] !== rng[0]
            ? Math.min(1, Math.max(0, (u - rng[0]) / (rng[1] - rng[0]))) : undefined;
        publish("scale", cfg.uid, {
            sx: currentSx,
            sy: currentSy,
            uniform: u,
            ...(t != null ? { t } : {})
        });

        // OSC output
        if (isOscEnabled(cfg, cfg.osc)) {
            sendOSC({
                addr: cfg.oscAddr || `scale/${cfg.uid}`,
                type: "osc_scale",
                uid: cfg.uid,
                sx: currentSx,
                sy: currentSy,
                avg: (currentSx + currentSy) / 2,
                timestamp: Date.now()
            });
        }
    }

    // Beat-visualiser decay state
    let _decayAnim = null;
    const _sxAddr  = isOscRef(sxRef) ? sxRef.address : (sxRef ? `${sxRef.source}.${sxRef.channel}` : null);
    const _syAddr  = isOscRef(syRef) ? syRef.address : (syRef ? `${syRef.source}.${syRef.channel}` : null);
    const _uniform = sxRef && syRef && (_sxAddr === _syAddr);

    function _startDecay() {
        if (!decayTime) return;
        if (_decayAnim) { _decayAnim.pause(); _decayAnim = null; }
        const proxy = { sx: currentSx, sy: currentSy };
        _decayAnim = anime.animate(proxy, {
            sx: 1.0,
            sy: 1.0,
            duration: decayTime * 1000,
            ease: animeEase(decayEasing),
            onUpdate: () => {
                currentSx = proxy.sx;
                currentSy = proxy.sy;
                updateScale();
            },
            onComplete: () => { _decayAnim = null; }
        });
    }

    /**
     * Apply one incoming value to the axis that asked for it.
     *
     * `axis` is "x", "y", or "both" (sx and sy bound to the same source, so one
     * subscription drives the pair).
     */
    function _applyValue(value, axis) {
        if (_decayAnim) { _decayAnim.pause(); _decayAnim = null; }
        if (axis === "y") {
            currentSy = value;
        } else {
            currentSx = value;
            if (axis === "both") currentSy = value;
        }
        updateScale();
        _startDecay();
    }

    // Bind sx
    if (sxRef) {
        const sxMin = sxRef.range?.[0] ?? 0.1;
        const sxMax = sxRef.range?.[1] ?? 3;
        if (sxHasExplicitDefault) registerFaderDefault(sxRef.source, sxRef.channel, sxMin, sxMax, sxDefault);

        bindParam(sxRef, (value) => {
            _applyValue(value, _uniform ? "both" : "x");
        }, { min: sxMin, max: sxMax, default: sxHasExplicitDefault ? sxDefault : undefined,
             owner: `scale:${cfg.uid || el.id}:sx` });
    }

    // Bind sy — skip if it shares the same address as sx (already handled above)
    if (syRef && !_uniform) {
        const syMin = syRef.range?.[0] ?? 0.1;
        const syMax = syRef.range?.[1] ?? 3;
        if (syHasExplicitDefault) registerFaderDefault(syRef.source, syRef.channel, syMin, syMax, syDefault);

        bindParam(syRef, (value) => {
            _applyValue(value, "y");
        }, { min: syMin, max: syMax, default: syHasExplicitDefault ? syDefault : undefined,
             owner: `scale:${cfg.uid || el.id}:sy` });
    }

    // The bindings above are owned by `scale:<uid>:sx|sy`, so re-running this
    // cue releases the previous pair rather than stacking another that still
    // writes to the old cfg.

    // Store state
    cfg._currentScale = { sx: currentSx, sy: currentSy };
}

// ============================================================================
// SCALE cue handler
// ============================================================================
/**
 * scale(1.5) -- a size to go to and stay at; scale(1.5, dur:2) glides there
 * from the size it has; scale(1.5, 0.8) x and y. Written as the sequences
 * the rest of this file plays: values:[1.5] (or [now, 1.5]), once.
 */
function fixedSizeArgs(args, el) {
    const isNum = v => typeof v === "number" || (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v)));
    const fixed = args.filter(a => ["values", "x", "y"].includes(a.key || a.type) && isNum(a.value));
    if (!fixed.length) return args;
    const glide = args.some(a => (a.key || a.type) === "dur");
    // the size it has: its wrapper's (ensureAnimWrapper -- a group's own, a leaf's parent)
    const w = el?._oscillaAnimWrapper || (el?.parentNode?.classList?.contains("oscilla-anim") ? el.parentNode : null);
    const m = String(w?.style?.transform || "")
        .match(/scale\(\s*([-\d.e]+)(?:\s*,\s*([-\d.e]+))?\s*\)/);
    const now = m ? [Number(m[1]), Number(m[2] ?? m[1])] : [1, 1];
    const out = args.map(a => {
        if (!fixed.includes(a)) return a;
        const k = a.key || a.type, n = Number(a.value);
        return { ...a, value: glide ? [k === "y" ? now[1] : now[0], n] : [n] };
    });
    if (!args.some(a => (a.key || a.type) === "loop")) out.push({ type: "loop", value: 1 });
    return out;
}

export function handleScaleCue(ast, el, options = {}) {
    if (!el) return;

    const { fromCueTrigger = false } = options;
    const astArgs = fixedSizeArgs(ast?.args || [], el);

    // -----------------------------------------------------------
    // CHECK: Is this a playhead trigger for already-registered element?
    // -----------------------------------------------------------
    const existingCfg = el._oscillaCfg;

    if (fromCueTrigger && existingCfg && existingCfg._armed) {
        if (needsArming(existingCfg)) {
            armElement(el, existingCfg);
        }
        return;
    }

    // ------------------------------------------------------------------------
    // Parse DSL args
    // ------------------------------------------------------------------------
    let trig = "auto";
    let cfgStartDelay = 0;
    let init = "show";

    let uid = null;
    let sync = true;  // Network sync enabled by default
    let syncExplicit = false;

    //  normalized OSC field
    let oscAddr = null;

    for (const a of astArgs) {
        const key = a.key || a.type;
        const val = a.value;

        if (key === "uid") uid = String(val).trim();
        if (key === "trig") trig = String(val).toLowerCase();
        if (key === "tdelay") cfgStartDelay = Number(secs(val)) || 0;   // beats
        if (key === "init") init = val;

        // accept BOTH spellings
        if (key === "oscaddr" || key === "oscAddr") {
            oscAddr = String(val).trim();
        }
        if (key === "sync") {
            syncExplicit = true;
            sync = !isOff(val);
        }
    }

    // Random patterns / rand() draw per client by design: no drift correction
    // unless sync:1 is authored (see isRandomised).
    if (!syncExplicit && astArgs.some(a => isRandomised(a.value))) sync = false;
    if (!uid) {
        uid = "scale_" + Math.random().toString(36).slice(2, 10);
    }

    const isScrollMode = window.mode === "scroll";
    // trig:<uid> waits for its source object (probeTriggers) even in page mode
    const shouldStartNow =
        fromCueTrigger || ((trig === "auto" || !isScrollMode) && !isObjectTrig(trig));

    // ------------------------------------------------------------------------
    // BASE CFG
    // ------------------------------------------------------------------------
    const baseCfg = {
        // the same pivot rotate() takes — see applySvgPivot above
        pivot: (ast?.args || astArgs || []).find?.(a => (a.key || a.type) === "pivot")?.value ?? null,
        uid,
        trig,
        start: cfgStartDelay,
        init,
        astArgs,
        fromCueTrigger,
        kind: "scale",

        oscAddr,       // 👈 canonical
        osc: 0,
        sync,          // Network sync enabled/disabled

        _anim: null,
        _start: null
    };

    el.style.pointerEvents = "all";
    el.parentNode?.appendChild(el);

    // ------------------------------------------------------------------------
    // Extract value args
    // ------------------------------------------------------------------------
    // values: a list or a pattern of sizes -- a sequence. values:follow(...)
    // is a size that follows a signal: the signal-bound mode below takes it.
    const valuesArg = astArgs.find(o => (o.key === "values" || o.type === "values")
        && !isSignalRef(o.value) && !isOscRef(o.value));
    const xArg = astArgs.find(o => ["x", "valuesX"].includes(o.key || o.type));
    const yArg = astArgs.find(o => ["y", "valuesY"].includes(o.key || o.type));

    const scaleWrapper = ensureAnimWrapper(el);

    function applyInitialScale(cfg) {
        // Pivot FIRST: without a transform-origin an SVG element scales about
        // the viewBox center, teleporting far from its slot — and the
        // visibility observer then pauses it offscreen, where it can never
        // resume (reads as an instant, permanent disappearance on trigger).
        applySvgPivot(scaleWrapper, cfg?.pivot);
        const sx = cfg.xValues?.[0] ?? cfg.values?.[0] ?? 1;
        const sy = cfg.yValues?.[0] ?? cfg.values?.[0] ?? sx;
        scaleWrapper.style.transform = `scale(${sx}, ${sy})`;
    }

    function wrapStart(cfg, rawStartFn) {
        return () => {
            const restoreOnly = () => applyPrestateOnStart(el, cfg);
            const fullStart = () => {
                applyPrestateOnStart(el, cfg);
                rawStartFn();
            };

            if (cfg.start > 0) {
                scheduleCueStart(cfg, el, () => {
                    if (cfg._armed && cfg._startBlocked) return restoreOnly();
                    fullStart();
                }, cfg.uid);

                if (cfg._armed && cfg._startBlocked) restoreOnly();
            } else {
                if (cfg._armed && cfg._startBlocked) return restoreOnly();
                fullStart();
            }
        };
    }

    // ------------------------------------------------------------------------
    // SEQUENCE uniform / pattern
    // ------------------------------------------------------------------------
    if (valuesArg) {
        const v = valuesArg.value;

        const cfg = {
            ...baseCfg,
            mode: Array.isArray(v) ? "sequence-uniform" : "sequence-pattern",
            values: Array.isArray(v) ? v : null,
            pattern: v?.type === "pattern" ? v : null
        };

        el._oscillaCfg = cfg;
        el.dataset.oscillaUid = cfg.uid;

        applyPrestateBeforeStart(el, cfg);
        installOscToggleHandler(el, cfg);
        installToggleStatesHandler(el, cfg);
        applyInitialScale(cfg);

        const rawStart = () => handleScaleSequence(el, cfg);
        cfg._start = wrapStart(cfg, rawStart);

        registerAnimation(el, "scale", cfg, cfg._start);

        if (shouldCreateHitLabel(cfg)) {
            createHitLabel(el, "scale", cfg.uid, {
                anchorMode: "pathMidPoint",
                color: "lime",
                sizeMode: "scale40"
            });
        }

        applyPrestateOnStart(el, cfg);
        if (shouldStartNow && !cfg._startBlocked) autoStartAnimation(cfg.uid, cfg._start, fromCueTrigger);
        return;
    }

    // ------------------------------------------------------------------------
    // SEQUENCE-XY
    // ------------------------------------------------------------------------
    if (xArg || yArg) {
        const cfg = {
            ...baseCfg,
            mode: "sequence-xy",
            xValues: Array.isArray(xArg?.value) ? xArg.value : null,
            yValues: Array.isArray(yArg?.value) ? yArg.value : null,
            xPattern: xArg?.value?.type === "pattern" ? xArg.value : null,
            yPattern: yArg?.value?.type === "pattern" ? yArg.value : null
        };

        el._oscillaCfg = cfg;
        el.dataset.oscillaUid = cfg.uid;

        applyPrestateBeforeStart(el, cfg);
        installOscToggleHandler(el, cfg);
        installToggleStatesHandler(el, cfg);
        applyInitialScale(cfg);

        const rawStart = () => handleScaleSequence(el, cfg);
        cfg._start = wrapStart(cfg, rawStart);

        registerAnimation(el, "scale", cfg, cfg._start);

        if (shouldCreateHitLabel(cfg)) {
            createHitLabel(el, "scale", cfg.uid, {
                anchorMode: "pathMidPoint",
                color: "lime",
                sizeMode: "scale40"
            });
        }

        applyPrestateOnStart(el, cfg);
        if (shouldStartNow && !cfg._startBlocked) autoStartAnimation(cfg.uid, cfg._start, fromCueTrigger);
        return;
    }

    // ------------------------------------------------------------------------
    // SIGNAL-BOUND MODE - check if sx or sy is a signal ref
    // ------------------------------------------------------------------------
    const sxArg = astArgs.find(o => o.key === "sx" || o.type === "sx");
    const syArg = astArgs.find(o => o.key === "sy" || o.type === "sy");
    const xyArg = astArgs.find(o => ["values", "uniform"].includes(o.key || o.type) && (isSignalRef(o.value) || isOscRef(o.value)));

    const hasSxSignal = sxArg && (isSignalRef(sxArg.value) || isOscRef(sxArg.value));
    const hasSySignal = syArg && (isSignalRef(syArg.value) || isOscRef(syArg.value));
    const hasXySignal = xyArg && (isSignalRef(xyArg.value) || isOscRef(xyArg.value));

    if (hasSxSignal || hasSySignal || hasXySignal) {
        const cfg = {
            ...baseCfg,
            mode: "signal-bound",
            _scaleWrapper: scaleWrapper
        };

        el._oscillaCfg = cfg;
        el.dataset.oscillaUid = cfg.uid;

        applyPrestateBeforeStart(el, cfg);
        installOscToggleHandler(el, cfg);
        installToggleStatesHandler(el, cfg);

        const rawStart = () => handleScaleSignalBound(el, cfg);
        cfg._start = wrapStart(cfg, rawStart);

        registerAnimation(el, "scale", cfg, cfg._start);

        if (shouldCreateHitLabel(cfg)) {
            createHitLabel(el, "scale", cfg.uid, {
                anchorMode: "pathMidPoint",
                color: "lime",
                sizeMode: "scale40"
            });
        }

        applyPrestateOnStart(el, cfg);

        // Signal-bound mode should ALWAYS start immediately - faders work regardless of scroll mode
        if (!cfg._startBlocked) {
            autoStartAnimation(cfg.uid, cfg._start, fromCueTrigger);
        }
        return;
    }

    // ------------------------------------------------------------------------
    // CONTINUOUS fallback
    // ------------------------------------------------------------------------
    const cfg = {
        ...baseCfg,
        mode: "continuous"
    };

    el._oscillaCfg = cfg;
    el.dataset.oscillaUid = cfg.uid;

    applyPrestateBeforeStart(el, cfg);
    installOscToggleHandler(el, cfg);
        installToggleStatesHandler(el, cfg);
    applyInitialScale(cfg);

    const rawStart = () => handleScaleContinuous(el, cfg);
    cfg._start = wrapStart(cfg, rawStart);

    registerAnimation(el, "scale", cfg, cfg._start);

    if (shouldCreateHitLabel(cfg)) {
        createHitLabel(el, "scale", cfg.uid, {
            anchorMode: "pathMidPoint",
            color: "lime",
            sizeMode: "scale40"
        });
    }

    applyPrestateOnStart(el, cfg);
    if (shouldStartNow && !cfg._startBlocked) autoStartAnimation(cfg.uid, cfg._start, fromCueTrigger);
}
